import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";
import { Printer } from "lucide-react";

/**
 * MRE Service Professional Metrics — weekly per-tech report.
 *
 * Phase 1: 100% manual entry. Every cell PATCHes back to
 * `/api/mre-service-pro-metrics` on blur (same pattern as l10/manual-metrics).
 * Layout mirrors the operations team's existing PDF so parity can be checked
 * side-by-side before any live wiring in Phase 2.
 *
 * Phase 2 (NOT this file): most cells will be computed from `kpi_jobs`.
 * A few need net-new ServiceTitan field mapping — flagged inline with `PHASE_2`
 * comments where each column is rendered.
 */

// Hardcoded Phase-1 roster. Order matches the PDF exactly — DO NOT alphabetize.
// Phase 2 will source this from tech_kpis so new hires appear automatically.
const ROSTER = [
  "Tanner Malone",
  "Curt McCarty",
  "James Pederson",
  "Nathanael Watts",
  "David Villapiano",
  "Ed Elliott",
  "Josh Buckner",
  "Joey Byers",
  "Chris Prather",
];

// Format helpers. `null`/`undefined` → empty string so blank cells render as blank
// (not "$0" or "0%") — makes it visually obvious which rows haven't been entered.
const fmtMoney = (v) => (v === null || v === undefined || v === "") ? "" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtInt = (v) => (v === null || v === undefined || v === "") ? "" : String(Math.round(Number(v)));
const fmtPct = (v) => (v === null || v === undefined || v === "") ? "" : `${Number(v).toFixed(1)}%`;
const fmtMoney2 = (v) => (v === null || v === undefined || v === "") ? "" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Single editable cell. Renders a formatted display value; on click, switches
 * to an <input> that commits on blur or Enter. Empty submission clears the
 * field (`null` on the wire, so partial-merge on the server won't retain
 * a stale value).
 *
 * `fmt` — display formatter (money/int/pct/money2)
 * `kind` — number input step ("1", "0.01", or "any")
 * `readOnly` — Phase-2 computed fields (`completed_revenue`, `converted_jobs`,
 *   `opp_job_average`, `opp_conversion_rate`) come from the server derived
 *   from `kpi_jobs`. They render with an "AUTO" badge and no click handler
 *   so operators can't accidentally overwrite them. The backend also strips
 *   these from `MRE_SP_ALLOWED_FIELDS` so a stray PATCH is a silent no-op.
 */
function EditableCell({ techName, field, value, fmt, kind = "1", testIdPrefix, onSaved, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { setDraft(value ?? ""); }, [value]);

  const commit = async () => {
    setErr(null);
    // Trim + coerce. Empty string → null (clears the cell).
    const raw = String(draft).trim();
    const next = raw === "" ? null : Number(raw);
    // Bail on no-op or NaN.
    if (raw !== "" && Number.isNaN(next)) { setErr("bad number"); return; }
    if (next === (value ?? null)) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch("/mre-service-pro-metrics", {
        tech_name: techName,
        [field]: next,
      });
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const testId = `${testIdPrefix}-${field}-${techName.toLowerCase().replace(/\s+/g, "-")}`;

  if (readOnly) {
    // Read-only Phase-2 computed cell. Uses the same right-aligned mono
    // formatting as editable cells so tables stay visually consistent —
    // only the background tint + "no cursor" hint distinguish it. Empty
    // values render blank (not "$0") to preserve the "no data yet" signal.
    return (
      <td
        data-testid={testId}
        className="border border-[#d1d5db] px-2 py-1 text-right text-[13px] font-mono-data bg-[#f5f6f8] text-[#0e1420] cursor-not-allowed"
        title="Computed from kpi_jobs — Phase 2 live wiring"
      >
        {fmt(value)}
      </td>
    );
  }

  if (editing) {
    return (
      <td className="border border-[#d1d5db] px-2 py-1 bg-[#fef9c3]">
        <input
          data-testid={`${testId}-input`}
          type="number"
          step={kind}
          value={draft}
          autoFocus
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); } }}
          className="w-full text-right text-[13px] font-mono-data bg-transparent outline-none"
        />
        {err && <div className="text-[10px] text-[#991b1b] mt-0.5">{err}</div>}
      </td>
    );
  }

  return (
    <td
      data-testid={testId}
      onClick={() => setEditing(true)}
      className="border border-[#d1d5db] px-2 py-1 text-right text-[13px] font-mono-data cursor-pointer hover:bg-[#f5f6f8]"
      title="Click to edit"
    >
      {fmt(value)}
    </td>
  );
}

// Table 1 columns — matches PDF exactly. `phase2` string documents the
// Phase-2 wiring plan for each column (LIVE = now wired 2026-02,
// CLEAN = ready to wire, INVESTIGATE = needs new ServiceTitan mapping).
//
// 2026-08-03 bug #3 fix: `completed_revenue` (the "Actual" column) shows
// cents (`fmtMoney2`) so we render $9,276.51 verbatim instead of dropping
// to $9,276. Every other money column stays whole-dollar because it's a
// manual entry or aggregate target — reserving 2-decimal precision for
// where it reflects a real ledger value.
const TABLE_1_COLS = [
  { field: "weekly_goal",         label: "Weekly Goal",          fmt: fmtMoney,  kind: "1",    phase2: "PRE-POPULATED — falls back to tech_goals[tech] for this week if unset; manual override wins. Clear the cell to revert to the tech_goals value." },
  { field: "completed_revenue",   label: "Completed Revenue",    fmt: fmtMoney2, kind: "0.01", phase2: "LIVE — sum(kpi_jobs.revenue) per tech per week (Phase 2 wired 2026-02). Cents shown so ledger matches ServiceTitan exactly." },
  { field: "total_sales",         label: "Total Sales",          fmt: fmtMoney,  kind: "1",    phase2: "INVESTIGATE — needs 'sold vs completed' distinction from ServiceTitan (revenue on sold-not-completed jobs)" },
  { field: "opportunity_count",   label: "Opportunity",          fmt: fmtInt,    kind: "1",    phase2: "INVESTIGATE — total call-in/lead count per tech; not currently in kpi_jobs schema. Feeds opp_conversion_rate when set." },
  { field: "converted_jobs",      label: "Converted Jobs",       fmt: fmtInt,    kind: "1",    phase2: "LIVE — count(kpi_jobs where status='completed' AND ptc_presented=YES) (Phase 2 wired 2026-02)" },
  { field: "opp_conversion_rate", label: "Opp Conversion Rate",  fmt: fmtPct,    kind: "0.1",  phase2: "LIVE — 100 * converted_jobs / opportunity_count. Renders blank until opportunity_count is entered manually." },
  { field: "opp_job_average",     label: "Opp Job Average",      fmt: fmtMoney2, kind: "0.01", phase2: "LIVE — completed_revenue / converted_jobs (Phase 2 wired 2026-02). Cents shown to mirror the ServiceTitan Big Board 'Sold Job Avg' precision." },
  { field: "rev_per_job_hour",    label: "Rev per Job Hour",     fmt: fmtMoney2, kind: "0.01", phase2: "INVESTIGATE — needs `hours` field on kpi_jobs; not currently ingested" },
];

// Table 2 columns.
const TABLE_2_COLS = [
  { field: "billable_efficiency",         label: "Billable Efficiency", fmt: fmtPct,   kind: "0.1",  phase2: "INVESTIGATE — needs billable_hours vs paid_hours per tech; not currently ingested" },
  { field: "warranty_jobs",               label: "Warranty Jobs",       fmt: fmtInt,   kind: "1",    phase2: "INVESTIGATE — needs `is_warranty` flag on kpi_jobs (ServiceTitan warranty jobType mapping)" },
  { field: "jobs_on_hold",                label: "Jobs on Hold",        fmt: fmtInt,   kind: "1",    phase2: "INVESTIGATE — needs `status='on_hold'` propagation from ServiceTitan; currently only 'completed' status flows" },
  { field: "smoke_detector_count",        label: "Smoke Detector #",    fmt: fmtInt,   kind: "1",    phase2: "INVESTIGATE — needs SKU/category mapping (which ServiceTitan business unit or SKU pattern counts as smoke detector)" },
  { field: "smoke_detector_revenue",      label: "Smoke Detector $",    fmt: fmtMoney, kind: "1",    phase2: "INVESTIGATE — same SKU/category mapping as smoke_detector_count" },
  { field: "surge_protection_count",      label: "Surge Protection #",  fmt: fmtInt,   kind: "1",    phase2: "INVESTIGATE — needs SKU/category mapping for surge protection line items" },
  { field: "surge_protection_revenue",    label: "Surge Protection $",  fmt: fmtMoney, kind: "1",    phase2: "INVESTIGATE — same SKU/category mapping as surge_protection_count" },
];

function MetricsTable({ title, cols, rows, computedFields, onSaved, testIdPrefix }) {
  return (
    <div className="rounded-xl border border-[#e7e9ee] bg-white overflow-hidden mre-sp-print-block" data-testid={`${testIdPrefix}-table`}>
      <div className="px-4 py-3 bg-[#0e1420] text-white">
        <h3 className="text-[13px] font-semibold uppercase tracking-widest">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="bg-[#f5f6f8]">
              <th className="border border-[#d1d5db] px-3 py-2 text-left font-semibold text-[#0e1420] sticky left-0 bg-[#f5f6f8] min-w-[140px]">
                Service Professional
              </th>
              {cols.map((c) => {
                const isAuto = computedFields.has(c.field);
                return (
                  <th
                    key={c.field}
                    className="border border-[#d1d5db] px-2 py-2 text-right font-semibold text-[#0e1420] whitespace-nowrap"
                    title={c.phase2}
                  >
                    <span>{c.label}</span>
                    {isAuto && (
                      <span
                        data-testid={`${testIdPrefix}-auto-badge-${c.field}`}
                        className="ml-1.5 inline-flex text-[9px] font-mono-data uppercase tracking-widest border border-[#0072bc] text-[#0072bc] bg-white rounded px-1 py-0.5 align-middle"
                        title="Automatically computed from kpi_jobs — not editable"
                      >
                        AUTO
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tech_name} data-testid={`${testIdPrefix}-row-${r.tech_name.toLowerCase().replace(/\s+/g, "-")}`}>
                <td className="border border-[#d1d5db] px-3 py-1.5 font-semibold text-[#0e1420] sticky left-0 bg-white">
                  {r.tech_name}
                </td>
                {cols.map((c) => (
                  <EditableCell
                    key={c.field}
                    techName={r.tech_name}
                    field={c.field}
                    value={r[c.field]}
                    fmt={c.fmt}
                    kind={c.kind}
                    testIdPrefix={testIdPrefix}
                    onSaved={onSaved}
                    readOnly={computedFields.has(c.field)}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MREServiceProMetrics() {
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });

  const load = useCallback(async () => {
    try {
      const { data: resp } = await api.get("/mre-service-pro-metrics");
      setData(resp);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Backend is the source of truth for which fields are computed vs. manual
  // (`data.computed_fields` on the GET response). We memoize a Set so cell
  // lookup is O(1) inside the render tree.
  const computedFieldsSet = useMemo(
    () => new Set(data?.computed_fields ?? []),
    [data?.computed_fields],
  );

  // Print / Export to PDF: uses the browser's native print flow so no PDF
  // library dependency. `@media print` CSS below (scoped via the
  // `mre-sp-print-root` marker class) hides everything else on the page and
  // renders ONLY the two tables in landscape orientation for TV/handout use.
  // The user's Save-to-PDF option in the print dialog produces the PDF.
  const onPrint = () => {
    // Toggle a body-level class first so the print CSS scope is easy to
    // reason about. Cleared automatically after the dialog closes because
    // `afterprint` fires whether the user prints or cancels.
    document.body.classList.add("mre-sp-print-active");
    const cleanup = () => {
      document.body.classList.remove("mre-sp-print-active");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  };

  if (loadState.status !== "ok" || !data) {
    return (
      <PanelState status={loadState.status} error={loadState.error} onRetry={load} testIdPrefix="mre-sp-metrics" />
    );
  }

  return (
    <section data-testid="mre-service-pro-metrics" className="space-y-4 mre-sp-print-root">
      {/* Print-only CSS. When body.mre-sp-print-active is set, hide           */}
      {/* everything except this section's print-block descendants, and lay   */}
      {/* out the two tables stacked in landscape with tight margins so the   */}
      {/* PDF handout looks like the ops team's existing weekly report.       */}
      <style>{`
        @media print {
          body.mre-sp-print-active > *:not(.mre-sp-print-portal),
          body.mre-sp-print-active .mre-sp-print-root ~ *,
          body.mre-sp-print-active *:not(.mre-sp-print-root, .mre-sp-print-root *, .mre-sp-print-header, .mre-sp-print-header *) {
            visibility: hidden !important;
          }
          body.mre-sp-print-active .mre-sp-print-root,
          body.mre-sp-print-active .mre-sp-print-root * {
            visibility: visible !important;
          }
          body.mre-sp-print-active .mre-sp-print-root {
            position: absolute; left: 0; top: 0; width: 100%; padding: 0.4in;
          }
          body.mre-sp-print-active .mre-sp-no-print { display: none !important; }
          body.mre-sp-print-active .mre-sp-print-block { break-inside: avoid; page-break-inside: avoid; margin-bottom: 12pt; }
          @page { size: landscape; margin: 0.4in; }
        }
      `}</style>
      <header className="flex items-baseline justify-between border-b border-[#e7e9ee] pb-2">
        <div>
          <h2 className="text-[15px] font-semibold text-[#0e1420] uppercase tracking-widest">
            MRE Service Professional Metrics
          </h2>
          <p className="text-[11px] text-[#6b7280] mt-0.5">
            Week of {data.week_of} · click any cell to edit ·
            <span className="italic ml-1">
              Phase 2 wired: {data.computed_fields?.length ?? 0} AUTO fields from kpi_jobs
              {" · "}remaining cells are manual entry
            </span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[11px] font-mono-data text-[#6b7280]">
            {data.rows.length} techs · {TABLE_1_COLS.length + TABLE_2_COLS.length} fields
          </div>
          <button
            type="button"
            onClick={onPrint}
            data-testid="mre-sp-print-btn"
            className="mre-sp-no-print inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#0e1420] bg-white border border-[#0e1420] rounded-md px-3 py-1.5 hover:bg-[#0e1420] hover:text-white transition-colors"
            title="Print or Save to PDF — landscape, tables only"
          >
            <Printer size={13} />
            Print / PDF
          </button>
        </div>
      </header>
      <MetricsTable
        title="Table 1 · Revenue & Conversion"
        cols={TABLE_1_COLS}
        rows={data.rows}
        computedFields={computedFieldsSet}
        onSaved={load}
        testIdPrefix="mre-sp-t1"
      />
      <MetricsTable
        title="Table 2 · Efficiency, Warranty, Add-On Sales"
        cols={TABLE_2_COLS}
        rows={data.rows}
        computedFields={computedFieldsSet}
        onSaved={load}
        testIdPrefix="mre-sp-t2"
      />
    </section>
  );
}
